/**
 * Mix design PDF — a print-styled rendering of the sheet.
 *
 * Same approach as ./pdf.ts, and for the same reasons: a self-contained HTML
 * document with an embedded stylesheet, handed to the browser's own print
 * dialog (Save as PDF). No PDF library is bundled, and the text stays real
 * text — selectable, searchable, and properly laid out.
 *
 * Everything except `printMixDesign` is PURE, which is what makes the layout
 * unit-testable without a DOM.
 *
 * The letterhead is SCI's own, transcribed from the printed sheet. What the
 * printed sheet also carries and this does not — the addressee, the job number
 * and the date — is deliberate: a mix design in this library is company-wide,
 * not a letter to one customer.
 */

import type { MixDesign, MixDesignMaterial } from '../types';
import { mixMassTotalKg } from '../calc';
import { escapeHtml, printHtmlDocument, sciLogoHtml, type PrintOptions } from './pdf';

export interface MixDesignPrintInput {
  mix_design: MixDesign;
  materials: readonly MixDesignMaterial[];
  /** Stamped in the document footer. Defaults to now. */
  exported_at?: string;
}

/** Empty values print as an em dash so a blank line is visibly blank. */
const EMPTY = '&mdash;';

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  return escapeHtml(value);
}

export const MIX_DESIGN_PRINT_STYLESHEET = `
  @page { size: letter portrait; margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    color: #10161d;
    background: #fff;
    font: 11pt/1.4 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
  }
  .doc { max-width: 190mm; margin: 0 auto; }
  /*
    The mark alone, centred. The padding above it is deliberate: @page already
    reserves a margin, but the mark reads as clipped when it starts hard against
    the top of the text block, so it is dropped a little further down the page.
  */
  .letterhead { padding-top: 4mm; margin-bottom: 8mm; text-align: center; }
  .letterhead h1 { margin: 0; font-size: 20pt; letter-spacing: -0.01em; }
  .letterhead h1 img { margin-inline: auto; }
  h2 { margin: 6mm 0 2mm; font-size: 12pt; border-bottom: 1.5pt solid #10161d; padding-bottom: 1mm; }
  table { border-collapse: collapse; width: 100%; font-size: 10pt; }
  th, td { border: 0.75pt solid #6b7784; padding: 1.6mm 2mm; text-align: left; vertical-align: top; }
  th { background: #eceff2; font-weight: 700; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total th, tr.total td { border-top: 2pt solid #10161d; font-weight: 700; }
  .fields td.k { width: 42%; font-weight: 600; background: #f5f7f9; }
  .notes { white-space: pre-wrap; }
  .foot { margin-top: 8mm; font-size: 8.5pt; color: #46525f; }
  /* Repeat the materials header on every page of a long sheet. */
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
`;

type Field = readonly [label: string, value: unknown];

function fieldTable(fields: readonly Field[]): string {
  const rows = fields
    .map(([label, value]) => `<tr><td class="k">${escapeHtml(label)}</td><td>${text(value)}</td></tr>`)
    .join('');
  return `<table class="fields"><tbody>${rows}</tbody></table>`;
}

function materialsTable(input: MixDesignPrintInput): string {
  const rows = input.materials
    .map(
      (material) =>
        `<tr>` +
        `<td>${text(material.name)}</td>` +
        `<td class="num">${text(material.amount)}${material.amount ? ` ${escapeHtml(material.unit)}` : ''}</td>` +
        `<td class="num">${text(material.density_kg_m3)}</td>` +
        `<td class="num">${text(material.volume_m3)}</td>` +
        `</tr>`
    )
    .join('');

  const total = mixMassTotalKg(
    input.materials.map((material) => ({
      name: material.name,
      amount: material.amount,
      unit: material.unit
    }))
  );

  return (
    `<table>` +
    `<thead><tr>` +
    `<th>Material</th><th class="num">SSD Proportions</th>` +
    `<th class="num">Density (kg/m³)</th><th class="num">Volume (m³)</th>` +
    `</tr></thead>` +
    `<tbody>${rows}</tbody>` +
    `<tfoot><tr class="total">` +
    `<th>Totals: Mass =</th><td class="num">${escapeHtml(String(total))} kg</td>` +
    `<th class="num">Yield (m³) =</th><td class="num">${text(input.mix_design.yield_m3)}</td>` +
    `</tr></tfoot>` +
    `</table>`
  );
}

export function buildMixDesignPrintBody(input: MixDesignPrintInput): string {
  const design = input.mix_design;
  const exportedAt = input.exported_at ?? new Date().toISOString();

  const wc =
    design.wc_ratio === null
      ? EMPTY
      : `${escapeHtml(String(design.wc_ratio))}${
          design.wc_ratio_max === null ? '' : `  (Max ${escapeHtml(String(design.wc_ratio_max))})`
        }`;

  const parts: string[] = [
    '<div class="doc">',
    '<div class="letterhead">',
    // The mark, centred, and nothing else. The postal address and phone
    // number that used to sit here were removed deliberately: this is a mix
    // statement, not correspondence. The mark IS the wordmark, so no company
    // name is typeset beside it either — its alt text keeps this `h1` reading
    // "Sustainable Concrete Inc." to a screen reader and to text extraction.
    `<h1>${sciLogoHtml(58)}</h1>`,
    '</div>',

    '<h2>Mix design</h2>',
    fieldTable([
      ['Project', design.project_name],
      ['Mix code', design.mix_code],
      ['Subject', design.subject]
    ]),

    '<h2>Materials</h2>',
    materialsTable(input),

    '<h2>Specification</h2>',
    fieldTable([
      ['Slump (mm)', design.slump],
      ['Specified compressive strength', design.specified_strength],
      ['Approx water cementitious ratio', wc]
    ])
  ];

  if (design.notes.trim() !== '') {
    parts.push('<h2>Notes</h2>', `<p class="notes">${escapeHtml(design.notes)}</p>`);
  }

  parts.push(
    '<p class="foot">',
    `Generated from the SCI mix design library on ${escapeHtml(exportedAt)}. `,
    'This sheet carries no signature block; it states the mix as designed.',
    '</p>',
    '</div>'
  );

  return parts.join('\n');
}

/**
 * Title for the browser tab and, in every current browser, the default
 * Save-as-PDF filename.
 */
export function mixDesignPrintTitle(input: MixDesignPrintInput): string {
  const { mix_code: code, project_name: project } = input.mix_design;
  const title = [code, project].filter((part) => part.trim() !== '').join(' ');
  return title === '' ? 'Mix design' : title;
}

export function buildMixDesignPrintHtml(input: MixDesignPrintInput): string {
  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(mixDesignPrintTitle(input))}</title>`,
    `<style>${MIX_DESIGN_PRINT_STYLESHEET}</style>`,
    '</head>',
    '<body>',
    buildMixDesignPrintBody(input),
    '</body>',
    '</html>',
    ''
  ].join('\n');
}

/** Build the sheet and open the print / Save-as-PDF dialog. */
export function printMixDesign(input: MixDesignPrintInput, options: PrintOptions = {}): boolean {
  return printHtmlDocument(buildMixDesignPrintHtml(input), options);
}
